import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useCart } from '../Context/CartContext';
import { useUser } from '../Context/UserContext';
import Toast from '../Components/Toast';
import '../Styles/Shop.css';

/* ─── helpers ─────────────────────────────────────────────────────── */
const isNew = (dateStr) => {
    if (!dateStr) return false;
    const diff = Date.now() - new Date(dateStr).getTime();
    return diff < 30 * 24 * 60 * 60 * 1000; // 30 days
};

const calculateDiscountedPrice = (price, type, value) => {
    const p = parseFloat(price);
    const v = parseFloat(value);
    if (!v || v <= 0) return p;
    if (type === 'percentage') {
        return p - (p * (v / 100));
    } else {
        return p - v;
    }
};

const getEffectiveDiscount = (product, variation) => {
    // If variation has an active discount, use it
    if (variation && variation.discount_is_active === 1 && parseFloat(variation.discount_value) > 0) {
        return {
            type: variation.discount_type,
            value: variation.discount_value
        };
    }
    // Otherwise use product-level discount if active
    if (product && product.discount_is_active === 1 && parseFloat(product.discount_value) > 0) {
        return {
            type: product.discount_type,
            value: product.discount_value
        };
    }
    return null;
};

const parseOfferDetails = (offerDetails) => {
    if (!offerDetails) return null;
    const [qty, price, groupId] = offerDetails.toString().split(':');
    const quantity = parseInt(qty, 10);
    const offerPrice = parseFloat(price);
    if (!quantity || quantity <= 0 || Number.isNaN(offerPrice)) return null;
    return { qty: quantity, price: offerPrice, groupId: groupId || null };
};

const isProductOutOfStock = (product) => {
    // Check if product has no main stock
    const mainStockEmpty = parseInt(product.StockQTY || 0) === 0;

    // Check if all variations are out of stock (varStock = 0)
    const varStockEmpty = parseInt(product.varStock || 0) === 0;

    // Product is out of stock only if both main and all variations are out
    return mainStockEmpty && varStockEmpty;
};

const getColorHex = (colorName) => {
    if (!colorName) return 'transparent';
    const normalized = colorName.toString().toLowerCase().trim();
    const map = {
        'navy blue': '#000080',
        'burgundy': '#800020',
        'khaki': '#F0E68C',
        'baby blue': '#89CFF0',
        'dusty pink': '#DCAE96',
        'white': '#ffffff',
        'black': '#000000',
        'red': '#ff0000',
        'green': '#008000',
        'blue': '#0000ff',
        'grey': '#808080',
        'gray': '#808080',
        'yellow': '#ffff00',
        'orange': '#ffa500',
        'purple': '#800080',
        'pink': '#ffc0cb',
        'brown': '#a52a2a',
        'beige': '#f5f5dc',
        'silver': '#c0c0c0',
        'gold': '#ffd700'
    };

    // 1. Check custom map
    if (map[normalized]) return map[normalized];

    // 2. Remove spaces to try to match native CSS extended colors (e.g., "Light Blue" -> "lightblue")
    const spaceless = normalized.replace(/\s+/g, '');
    if (typeof CSS !== 'undefined' && CSS.supports && CSS.supports('color', spaceless)) {
        return spaceless;
    }

    // 3. Unrecognized color
    return null;
};

const getSizeGroup = (sizeName) => {
    if (!sizeName) return '';
    const norm = sizeName.toString().trim().toUpperCase();

    // Group S-M
    if (norm === 'S' || norm === 'M' || norm === 'SMALL' || norm === 'MEDIUM' || norm === 'S-M' || norm === 'S - M') {
        return 'S-M';
    }

    // Group L-XL
    if (norm === 'L' || norm === 'XL' || norm === 'LARGE' || norm === 'EXTRA LARGE' || norm === 'L-XL' || norm === 'L - XL') {
        return 'L-XL';
    }

    // Group 2XL-3XL
    if (norm === '2XL' || norm === '3XL' || norm === 'XXL' || norm === 'XXXL' || norm === '2XL-3XL' || norm === '2XL - 3XL' || norm === 'XXL-XXXL') {
        return '2XL-3XL';
    }

    // Standalone / default
    return sizeName;
};

const sortSizeGroups = (groups) => {
    const order = {
        'xs': 1, 'extra small': 1,
        's-m': 2, 's - m': 2,
        'l-xl': 3, 'l - xl': 3,
        '2xl-3xl': 4, '2xl - 3xl': 4, 'xxl-xxxl': 4
    };
    return [...groups].sort((a, b) => {
        const aVal = a.name.toLowerCase();
        const bVal = b.name.toLowerCase();

        if (order[aVal] && order[bVal]) return order[aVal] - order[bVal];
        if (order[aVal]) return -1;
        if (order[bVal]) return 1;

        return aVal.localeCompare(bVal);
    });
};

const sortSizes = (sizes) => {
    const sizeOrder = {
        'xs': 1, 'extra small': 1,
        's': 2, 'small': 2,
        'm': 3, 'medium': 3,
        'l': 4, 'large': 4,
        'xl': 5, 'extra large': 5,
        'xxl': 6, '2xl': 6,
        'xxxl': 7, '3xl': 7
    };

    return [...sizes].sort((a, b) => {
        const aVal = a.toString().toLowerCase();
        const bVal = b.toString().toLowerCase();

        // 1. Check custom order map
        if (sizeOrder[aVal] && sizeOrder[bVal]) return sizeOrder[aVal] - sizeOrder[bVal];
        if (sizeOrder[aVal]) return -1;
        if (sizeOrder[bVal]) return 1;

        // 2. Try numeric sort
        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);
        if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;

        // 3. Fallback to alphabetical
        return aVal.localeCompare(bVal);
    });
};

/* ─── skeleton card ───────────────────────────────────────────────── */
const SkeletonCard = () => (
    <div className="product-card skeleton-card">
        <div className="skeleton skeleton-img" />
        <div className="skeleton skeleton-tag" />
        <div className="skeleton skeleton-title" />
        <div className="skeleton skeleton-price" />
    </div>
);

/* ─── main component ──────────────────────────────────────────────── */
const Shop = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [seasons, setSeasons] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [subSubCategories, setSubSubCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('newest');
    const [priceMin, setPriceMin] = useState('');
    const [priceMax, setPriceMax] = useState('');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productVariations, setProductVariations] = useState([]);
    const [productVariationsLoading, setProductVariationsLoading] = useState(false);
    const [attributeValues, setAttributeValues] = useState([]);
    const preloadedPhotosRef = useRef({});
    const [preloadedPhotos, setPreloadedPhotos] = useState({});
    const prefetchedProductsRef = useRef(new Set());
    const [sizeScales, setSizeScales] = useState([]);
    const [selectedColor, setSelectedColor] = useState(null);
    const [selectedSize, setSelectedSize] = useState(null);
    const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);
    const [quantity, setQuantity] = useState(1);
    const [saleOnly, setSaleOnly] = useState(searchParams.get('filter') === 'sale');
    const [loading, setLoading] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [expandedCats, setExpandedCats] = useState({});
    const [deliverySettingsList, setDeliverySettingsList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(52); // Items per page
    const [hiddenItems, setHiddenItems] = useState([]);
    const { addToCart, cart } = useCart();
    const { user } = useUser();
    const [notification, setNotification] = useState(null);
    const [wishlistSet, setWishlistSet] = useState(new Set());
    const modalRef = useRef(null);
    const searchRef = useRef(null);
    const rootRef = useRef(null);

    const catID = searchParams.get('catID');
    const subCatID = searchParams.get('subCatID');
    const subSubID = searchParams.get('subSubID');
    const seasonID = searchParams.get('seasonID');

    // Automatically set existing quantity if editing this product/variation in the order
    useEffect(() => {
        if (selectedProduct) {
            const editingOrderId = sessionStorage.getItem('editingOrderId');
            if (editingOrderId) {
                const storedItems = JSON.parse(sessionStorage.getItem('editingOrderItems') || '[]');
                let matchedVar = null;
                if (productVariations.length > 0) {
                    matchedVar = productVariations.find(v =>
                        (!selectedColor || v.Color === selectedColor) &&
                        (!selectedSize || v.Size === selectedSize)
                    );
                }
                const exists = storedItems.find(item =>
                    item.product_id === selectedProduct.id &&
                    item.variation_id === (matchedVar ? matchedVar.varID : null)
                );
                if (exists) {
                    setQuantity(Number(exists.quantity) || 1);
                } else {
                    setQuantity(1);
                }
            }
        }
    }, [selectedProduct, selectedColor, selectedSize, productVariations]);

    // eslint-disable-next-line no-unused-vars
    const handleExchangeSubmitFromShop = async (matchedVar) => {
        const exchangingOrderId = sessionStorage.getItem('exchangingOrderId');
        const exchangingItemStr = sessionStorage.getItem('exchangingItem');
        if (!exchangingOrderId || !exchangingItemStr || !selectedProduct) return;

        const returningItem = JSON.parse(exchangingItemStr);

        try {
            // Get active discount for the new product
            const discountInfo = getEffectiveDiscount(selectedProduct, matchedVar);
            const basePrice = parseFloat(selectedProduct.Price || 0);
            const finalPrice = discountInfo
                ? calculateDiscountedPrice(basePrice, discountInfo.type, discountInfo.value)
                : basePrice;

            const total = finalPrice * quantity;
            const discountAmount = (basePrice - finalPrice) * quantity;

            const exchangeFooters = [{
                original_order_footer_id: returningItem.id,
                product_id: selectedProduct.id,
                variation_id: matchedVar ? matchedVar.varID : null,
                quantity: quantity,
                price: basePrice,
                discount: discountAmount,
                total: total,
                offer_id: selectedProduct.offer_qty && selectedProduct.offer_price ? 1 : null,
                offer_qty: selectedProduct.offer_qty || null,
                offer_price: selectedProduct.offer_price || null
            }];

            const payload = {
                original_order_id: parseInt(exchangingOrderId),
                total_amount: basePrice * quantity,
                discount_amount: discountAmount,
                delivery_fee: 0.00,
                final_amount: total,
                status: 'pending',
                notes: `Exchanged returning item '${returningItem.product_name}' (Qty: ${returningItem.exchangeQty}) for new item '${selectedProduct.name}' (Qty: ${quantity}) via Shop Page.`,
                items: exchangeFooters
            };

            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/orders/exchanges`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to submit exchange request');
            }

            alert(`Exchange request EXC-${data.exchangeId} submitted successfully!`);

            // Clear exchange keys
            sessionStorage.removeItem('exchangingOrderId');
            sessionStorage.removeItem('exchangingOrderNumber');
            sessionStorage.removeItem('exchangingItem');

            setSelectedProduct(null);
            navigate('/orders');
        } catch (err) {
            alert('Exchange failed: ' + err.message);
        }
    };

    /* helpers ────────────────────────────────────────────────────── */
    const toggleCat = (id) => setExpandedCats(prev => prev[id] ? {} : { [id]: true });

    const clearFilters = () => {
        setSearchTerm('');
        setPriceMin('');
        setPriceMax('');
        setSortBy('newest');
        setSaleOnly(false);
        navigate('/shop'); // Explicitly navigate to /shop to clear all query params
    };

    /* fetch ──────────────────────────────────────────────────────── */
    useEffect(() => {
        const fetchData = async () => {
            const cachedShopData = sessionStorage.getItem('shopData');
            if (cachedShopData) {
                try {
                    const parsed = JSON.parse(cachedShopData);
                    setProducts(parsed.products || []);
                    setCategories(parsed.categories || []);
                    setSubCategories(parsed.subCategories || []);
                    setSubSubCategories(parsed.subSubCategories || []);
                    setHiddenItems(parsed.hiddenItems || []);
                    setAttributeValues(parsed.attributeValues || []);
                    setSeasons(parsed.seasons || []);
                    setDeliverySettingsList(parsed.deliverySettingsList || []);
                    setLoading(false);
                } catch (e) {
                    console.warn('Failed to parse shopData cache', e);
                }
            } else {
                setLoading(true);
            }

            try {
                const [prodRes, catRes, subRes, ssubRes, delivRes, hiddenRes, attrValRes, seasonRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/admin/products`),
                    axios.get(`${API_BASE_URL}/admin/categories`),
                    axios.get(`${API_BASE_URL}/admin/subcategories`),
                    axios.get(`${API_BASE_URL}/admin/subsubcategories`),
                    axios.get(`${API_BASE_URL}/admin/delivery-settings`),
                    axios.get(`${API_BASE_URL}/admin/hidden`),
                    axios.get(`${API_BASE_URL}/admin/attribute-values`),
                    axios.get(`${API_BASE_URL}/admin/seasons`)
                ]);
                setProducts(prodRes.data);
                setCategories(catRes.data);
                setSubCategories(subRes.data);
                setSubSubCategories(ssubRes.data);
                setHiddenItems(hiddenRes.data || []);
                setAttributeValues(attrValRes.data || []);
                setSeasons(seasonRes.data || []);

                let delivList = [];
                if (delivRes.data && delivRes.data.length > 0) {
                    delivList = delivRes.data;
                    setDeliverySettingsList(delivList);
                }

                sessionStorage.setItem('shopData', JSON.stringify({
                    products: prodRes.data,
                    categories: catRes.data,
                    subCategories: subRes.data,
                    subSubCategories: ssubRes.data,
                    hiddenItems: hiddenRes.data || [],
                    attributeValues: attrValRes.data || [],
                    seasons: seasonRes.data || [],
                    deliverySettingsList: delivList
                }));
            } catch (err) {
                console.error('Shop fetch error:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    /* expand active category by default ─────────────────────────── */
    useEffect(() => {
        if (catID) setExpandedCats({ [catID]: true });
        if (searchParams.get('filter') === 'sale') setSaleOnly(true);
    }, [catID, searchParams]);

    // Fetch user's wishlist IDs to prevent duplicate additions
    useEffect(() => {
        if (!user) {
            setWishlistSet(new Set());
            return;
        }
        axios.get(`${API_BASE_URL}/user/wishlist/${user.id}`)
            .then(res => {
                const set = new Set((res.data || []).map(it => `${it.product_id}:${it.variation_id === null ? 'null' : it.variation_id}`));
                setWishlistSet(set);
            })
            .catch(err => console.error('Error fetching wishlist:', err));
    }, [user]);

    const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
    const activeDeliverySetting = deliverySettingsList.length > 0
        ? (user?.city
            ? deliverySettingsList.find(item => item.city && item.city.trim().toLowerCase() === user.city.trim().toLowerCase())
            : null) || deliverySettingsList.find(item => (item.city || '').trim().toLowerCase() === 'default') || deliverySettingsList.find(item => !item.city || item.city.trim() === '') || deliverySettingsList[0]
        : null;

    useEffect(() => {
        const handleScroll = () => {
            setIsHeaderScrolled(window.scrollY > 100);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    /* keyboard shortcuts ─────────────────────────────────────────── */
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') setSelectedProduct(null);
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                searchRef.current?.focus();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const getAttributeName = (idOrCode) => {
        if (idOrCode === null || idOrCode === undefined || idOrCode === '') return '';
        const normalized = idOrCode.toString().trim();
        const input = normalized.toLowerCase();
        const attr = attributeValues.find(av => {
            const avId = av.id?.toString().trim();
            const avCode = (av.code || av.Code)?.toString().trim().toLowerCase();
            return avId === normalized || avCode === input;
        });
        if (!attr) return normalized;
        return attr.value || attr.Value || normalized;
    };

    /* lock scroll when modal open & fetch variations ────────────────── */
    useEffect(() => {
        if (selectedProduct) {
            document.body.style.overflow = 'hidden';
            setQuantity(1);
            setProductVariationsLoading(true);
            axios.get(`${API_BASE_URL}/admin/variations?prodID=${selectedProduct.id}`)
                .then(res => {
                    let vars = res.data.map(v => ({
                        ...v,
                        Color: v.ColorName || getAttributeName(v.Color),
                        Size: v.SizeName || getAttributeName(v.Size)
                    }));

                    // Sort variations to prioritize the one shown in the shop grid or marked as main
                    vars.sort((a, b) => {
                        // 1. Exact match with the photo shown in the grid
                        if (selectedProduct.varPhoto) {
                            if (a.photo === selectedProduct.varPhoto && b.photo !== selectedProduct.varPhoto) return -1;
                            if (b.photo === selectedProduct.varPhoto && a.photo !== selectedProduct.varPhoto) return 1;
                        }
                        // 2. isMain priority
                        if (a.isMain !== b.isMain) return b.isMain - a.isMain;
                        // 3. Variations with photos first
                        if (a.photo && !b.photo) return -1;
                        if (!a.photo && b.photo) return 1;
                        return 0;
                    });

                    setProductVariations(vars);
                    // Preload variation photos to make color/image switches snappier
                    try {
                        const photosToPreload = new Set();
                        vars.forEach(v => {
                            if (v.photo) photosToPreload.add(v.photo);
                        });
                        if (selectedProduct?.varPhoto) photosToPreload.add(selectedProduct.varPhoto);
                        if (selectedProduct?.photo) photosToPreload.add(selectedProduct.photo);

                        photosToPreload.forEach(photo => {
                            const url = `${API_BASE_URL}${photo}`;
                            if (preloadedPhotosRef.current[url]) return;
                            preloadedPhotosRef.current[url] = 'loading';
                            const img = new Image();
                            img.src = url;
                            img.onload = () => setPreloadedPhotos(prev => ({ ...prev, [url]: true }));
                            img.onerror = () => setPreloadedPhotos(prev => ({ ...prev, [url]: false }));
                        });
                    } catch (e) {
                        console.error('Preload photos error', e);
                    }
                    const colors = [...new Set(vars.map(v => v.Color).filter(Boolean))];

                    if (colors.length > 0) {
                        setSelectedColor(colors[0]);
                        const sizesForColor = sortSizes([...new Set(vars.filter(v => v.Color === colors[0]).map(v => v.Size).filter(Boolean))]);
                        if (sizesForColor.length > 0) setSelectedSize(sizesForColor[0]);
                    } else {
                        const sizes = sortSizes([...new Set(vars.map(v => v.Size).filter(Boolean))]);
                        if (sizes.length > 0) setSelectedSize(sizes[0]);
                    }
                })
                .catch(err => console.error('Shop variation fetch error:', err))
                .finally(() => setProductVariationsLoading(false));

            axios.get(`${API_BASE_URL}/admin/size-scales?product_id=${selectedProduct.id}`)
                .then(res => {
                    setSizeScales(res.data || []);
                })
                .catch(err => console.error('Shop size scales fetch error:', err));
        } else {
            setProductVariations([]);
            setSizeScales([]);
            setSelectedColor(null);
            setSelectedSize(null);
            setIsSizeGuideOpen(false);
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [selectedProduct, attributeValues]);

    const handleAddToWishlist = async () => {
        if (!user) {
            setNotification({ message: 'Please log in to add items to your wishlist', type: 'error' });
            return;
        }

        let matchedVar = null;
        if (productVariations.length > 0) {
            matchedVar = productVariations.find(v =>
                (!selectedColor || v.Color === selectedColor) &&
                (!selectedSize || v.Size === selectedSize)
            );
        }
        const key = `${selectedProduct.id}:${matchedVar ? matchedVar.varID : 'null'}`;
        if (wishlistSet.has(key)) {
            setNotification({ message: 'Product already in wishlist', type: 'error' });
            return;
        }

        try {
            const res = await axios.post(`${API_BASE_URL}/user/wishlist`, {
                user_id: user.id,
                product_id: selectedProduct.id,
                variation_id: matchedVar ? matchedVar.varID : null
            });
            setNotification({ message: res.data && res.data.message ? res.data.message : 'Added to wishlist!', type: 'success' });
            setWishlistSet(prev => new Set(prev).add(key));
        } catch (err) {
            if (err.response && err.response.data && err.response.data.message) {
                setNotification({ message: err.response.data.message, type: 'error' });
            } else {
                console.error('Error adding to wishlist:', err);
                setNotification({ message: 'Failed to add to wishlist', type: 'error' });
            }
        }
    };


    /* derived data ───────────────────────────────────────────────── */
    const structuredCategories = useMemo(() => (
        categories.map(cat => ({
            ...cat,
            subCategories: subCategories.filter(s => s.category_id === cat.id).map(sub => ({
                ...sub,
                specialized: subSubCategories.filter(ss => ss.subcategory_id === sub.id),
            })),
        }))
    ), [categories, subCategories, subSubCategories]);

    const breadcrumbs = useMemo(() => {
        const crumbs = [];
        if (catID) {
            const cat = categories.find(c => c.id === parseInt(catID));
            if (cat) crumbs.push({ label: cat.name, path: `/shop?catID=${cat.id}` });
        }
        if (subCatID) {
            const sub = subCategories.find(s => s.id === parseInt(subCatID));
            if (sub) crumbs.push({ label: sub.name, path: `/shop?catID=${sub.category_id}&subCatID=${sub.id}` });
        }
        if (subSubID) {
            const ssub = subSubCategories.find(s => s.id === parseInt(subSubID));
            if (ssub) crumbs.push({ label: ssub.name, path: `/shop?catID=${catID}&subCatID=${subCatID}&subSubID=${ssub.id}` });
        }
        return crumbs;
    }, [catID, subCatID, subSubID, categories, subCategories, subSubCategories]);

    const currentTitle = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].label : 'All Collections';

    const filteredProducts = useMemo(() => {
        const min = parseFloat(priceMin) || 0;
        const max = parseFloat(priceMax) || Infinity;
        return products
            .filter(p => {
                const matchesCat = subSubID ? p.subSubID === parseInt(subSubID)
                    : subCatID ? p.subCatID === parseInt(subCatID)
                        : catID ? p.catID === parseInt(catID) : true;
                const matchesSearch = !searchTerm ||
                    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (p.description && p.description.toLowerCase().includes(searchTerm.toLowerCase()));
                const basePrice = parseFloat(p.Price || 0);
                const effectivePrice = (p.discount_is_active === 1 && parseFloat(p.discount_value) > 0)
                    ? calculateDiscountedPrice(basePrice, p.discount_type, p.discount_value)
                    : basePrice;

                const isHidden = hiddenItems.some(h => h.product_id === p.id && h.variation_id === null && h.isHidden === 1);
                if (isHidden) return false;

                const activeSeasonIds = seasons.filter(s => s.selected === 1 || s.selected === true).map(s => s.id);
                const matchesSeason = seasonID
                    ? p.season === parseInt(seasonID)
                    : (activeSeasonIds.length > 0 ? activeSeasonIds.includes(p.season) : (p.season != null && p.season !== ''));

                const matchesPrice = effectivePrice >= min && effectivePrice <= max;
                const matchesSale = !saleOnly || (p.discount_is_active === 1 && parseFloat(p.discount_value) > 0) || (p.variationDiscountCount > 0) || (p.activeOfferCount > 0);
                return matchesCat && matchesSearch && matchesPrice && matchesSale && matchesSeason;
            })
            .sort((a, b) => {
                if (sortBy === 'price-low') return parseFloat(a.Price) - parseFloat(b.Price);
                if (sortBy === 'price-high') return parseFloat(b.Price) - parseFloat(a.Price);
                if (sortBy === 'name-az') return a.name.localeCompare(b.name);
                return new Date(b.Date || 0) - new Date(a.Date || 0); // newest
            });
    }, [products, catID, subCatID, subSubID, seasonID, searchTerm, sortBy, priceMin, priceMax, saleOnly, hiddenItems, seasons]);


    /* Pagination Logic */
    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
    const currentProducts = useMemo(() => {
        const indexOfLastItem = currentPage * itemsPerPage;
        const indexOfFirstItem = indexOfLastItem - itemsPerPage;
        return filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
    }, [filteredProducts, currentPage, itemsPerPage]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [catID, subCatID, subSubID, seasonID, searchTerm, sortBy, priceMin, priceMax, saleOnly]);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Prefetch variations (and their photos) for visible products to make color switches fast
    useEffect(() => {
        const PREFETCH_LIMIT = Math.min(currentProducts.length, 8);
        currentProducts.slice(0, PREFETCH_LIMIT).forEach(prod => {
            if (prefetchedProductsRef.current.has(prod.id)) return;
            prefetchedProductsRef.current.add(prod.id);

            axios.get(`${API_BASE_URL}/admin/variations?prodID=${prod.id}`)
                .then(res => {
                    const vars = res.data || [];
                    vars.forEach(v => {
                        if (v.photo) {
                            const url = `${API_BASE_URL}${v.photo}`;
                            if (preloadedPhotosRef.current[url]) return;
                            preloadedPhotosRef.current[url] = 'loading';
                            const img = new Image();
                            img.src = url;
                            img.onload = () => setPreloadedPhotos(prev => ({ ...prev, [url]: true }));
                            img.onerror = () => setPreloadedPhotos(prev => ({ ...prev, [url]: false }));
                        }
                    });
                })
                .catch(err => {
                    // non-fatal
                });
        });
    }, [currentProducts]);
    const hasActiveFilters = Boolean(searchTerm || priceMin || priceMax || sortBy !== 'newest' || saleOnly || seasonID);

    /* ── render ─────────────────────────────────────────────────── */
    return (
        <div className="shop-root" ref={rootRef}>
            {/* Mobile sidebar backdrop */}
            {sidebarOpen && (
                <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
            )}

            {/* ── sidebar ───────────────────────────────────────── */}
            <aside className={`shop-sidebar ${sidebarOpen ? 'open' : ''}`}>
                <div className="sidebar-inner">
                    {/* Categories */}
                    <div className="sidebar-section">
                        <ul className="sidebar-nav">
                            <li>
                                <button
                                    type="button"
                                    className={`snav-item ${!catID && !subCatID && !subSubID ? 'active' : ''}`}
                                    onClick={() => {
                                        clearFilters();
                                        setSidebarOpen(false);
                                    }}
                                >
                                    All Items
                                </button>
                            </li>
                            {structuredCategories.map(cat => (
                                <li key={cat.id} className="snav-group">
                                    <div className="snav-cat-row">
                                        <Link
                                            to={`/shop?catID=${cat.id}`}
                                            className={`snav-item snav-cat ${catID === String(cat.id) ? 'active' : ''}`}
                                            onClick={(e) => {
                                                if (cat.subCategories.length > 0) {
                                                    toggleCat(cat.id);
                                                }
                                                setSidebarOpen(false);
                                            }}
                                        >
                                            {cat.name}
                                        </Link>
                                        {cat.subCategories.length > 0 && (
                                            <button
                                                className={`snav-toggle ${expandedCats[cat.id] ? 'open' : ''}`}
                                                onClick={() => toggleCat(cat.id)}
                                                aria-label={expandedCats[cat.id] ? 'Collapse' : 'Expand'}
                                            >
                                                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                                    <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                    {expandedCats[cat.id] && cat.subCategories.length > 0 && (
                                        <ul className="snav-sub">
                                            {cat.subCategories.map(sub => (
                                                <li key={sub.id}>
                                                    <Link
                                                        to={`/shop?catID=${cat.id}&subCatID=${sub.id}`}
                                                        className={`snav-item snav-sub-item ${subCatID === String(sub.id) ? 'active' : ''}`}
                                                        onClick={() => setSidebarOpen(false)}
                                                    >
                                                        {sub.name}
                                                    </Link>
                                                    {sub.specialized.length > 0 && (
                                                        <ul className="snav-ssub">
                                                            {sub.specialized.map(ss => (
                                                                <li key={ss.id}>
                                                                    <Link
                                                                        to={`/shop?catID=${cat.id}&subCatID=${sub.id}&subSubID=${ss.id}`}
                                                                        className={`snav-item snav-ssub-item ${subSubID === String(ss.id) ? 'active' : ''}`}
                                                                        onClick={() => setSidebarOpen(false)}
                                                                    >
                                                                        {ss.name}
                                                                    </Link>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Seasons */}
                    <div className="sidebar-section">
                        <p className="sidebar-label">Season</p>
                        <select
                            className="sort-select-sidebar"
                            value={seasonID || ''}
                            onChange={(e) => {
                                const val = e.target.value;
                                const params = new URLSearchParams(searchParams);
                                if (val) params.set('seasonID', val);
                                else params.delete('seasonID');
                                navigate(`/shop?${params.toString()}`);
                                setSidebarOpen(false);
                            }}
                        >
                            <option value="">All Seasons</option>
                            {seasons.filter(s => s.selected === 1 || s.selected === true).map(s => (
                                <option key={s.id} value={s.id}>{s.name} {s.year}</option>
                            ))}
                        </select>
                    </div>

                    {/* Sort & Promotions Row */}
                    <div className="sidebar-section">
                        <div className="sidebar-row">
                            <div className="sidebar-col">
                                <p className="sidebar-label">Sort By</p>
                                <select
                                    className="sort-select-sidebar"
                                    value={sortBy}
                                    onChange={(e) => {
                                        setSortBy(e.target.value);
                                        setSidebarOpen(false);
                                    }}
                                >
                                    <option value="newest">Newest</option>
                                    <option value="price-low">Price: Low-High</option>
                                    <option value="price-high">Price: High-Low</option>
                                    <option value="name-az">A-Z</option>
                                </select>
                            </div>
                            <div className="sidebar-col">
                                <p className="sidebar-label">Promotions</p>
                                <div className="filter-checkbox-group">
                                    <label className="filter-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={saleOnly}
                                            onChange={(e) => setSaleOnly(e.target.checked)}
                                        />
                                        <span className="custom-checkbox"></span>
                                        Sale Only
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Price Range */}
                    <div className="sidebar-section">
                        <p className="sidebar-label">Price Range</p>
                        <div className="price-inputs">
                            <input
                                type="number"
                                placeholder="Min"
                                value={priceMin}
                                onChange={e => setPriceMin(e.target.value)}
                                className="price-input"
                            />
                            <span className="price-sep">—</span>
                            <input
                                type="number"
                                placeholder="Max"
                                value={priceMax}
                                onChange={e => setPriceMax(e.target.value)}
                                className="price-input"
                            />
                        </div>
                    </div>



                    {hasActiveFilters && (
                        <button className="clear-filters-btn" onClick={clearFilters}>
                            Clear all filters
                        </button>
                    )}
                </div>
            </aside>

            {/* ── main ──────────────────────────────────────────── */}
            <div className="shop-main">

                {/* hero header & breadcrumbs */}
                <div className={`shop-top-section ${isHeaderScrolled ? 'scrolled' : ''}`}>
                    <nav className="breadcrumb-nav" aria-label="Breadcrumb">
                        {breadcrumbs.map((crumb, i) => (
                            <React.Fragment key={crumb.path}>
                                <Link
                                    to={crumb.path}
                                    className={`bc-link ${i === breadcrumbs.length - 1 ? 'active' : ''}`}
                                >
                                    {crumb.label}
                                </Link>
                                {i < breadcrumbs.length - 1 && <span className="bc-sep">›</span>}
                            </React.Fragment>
                        ))}
                    </nav>

                    <div className="shop-header-main">
                        <header className="shop-header">
                            <h1 className="shop-title">{currentTitle}</h1>
                        </header>

                        <div className="search-bar" onClick={() => searchRef.current?.focus()}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="search-icon">
                                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                            <input
                                ref={searchRef}
                                type="text"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                aria-label="Search products"
                            />
                            {searchTerm && (
                                <button className="search-clear" onClick={(e) => { e.stopPropagation(); setSearchTerm(''); }} aria-label="Clear search">
                                    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="1" y1="1" x2="13" y2="13" /><line x1="13" y1="1" x2="1" y2="13" />
                                    </svg>
                                </button>
                            )}
                        </div>

                        <div className="header-filters-desktop">
                            <div className="header-filter-group">
                                <div className="header-price-range-desktop">
                                    <input
                                        type="number"
                                        placeholder="Min $"
                                        value={priceMin}
                                        onChange={e => setPriceMin(e.target.value)}
                                        className="header-price-input"
                                        aria-label="Min price"
                                    />
                                    <span className="header-price-sep">—</span>
                                    <input
                                        type="number"
                                        placeholder="Max $"
                                        value={priceMax}
                                        onChange={e => setPriceMax(e.target.value)}
                                        className="header-price-input"
                                        aria-label="Max price"
                                    />
                                </div>
                            </div>

                            <div className="header-filter-group">
                                <label className={`header-sale-toggle ${saleOnly ? 'is-active' : ''}`} title="Filter sale items only">
                                    <input
                                        type="checkbox"
                                        checked={saleOnly}
                                        onChange={(e) => setSaleOnly(e.target.checked)}
                                    />
                                    <span className="sale-toggle-track">
                                        <span className="sale-toggle-thumb" />
                                    </span>
                                    <span className="sale-toggle-text">sale only</span>
                                </label>
                            </div>

                            <div className="header-filter-group">
                                <select
                                    className="sort-select-desktop"
                                    value={sortBy}
                                    onChange={e => setSortBy(e.target.value)}
                                    aria-label="Sort products"
                                >
                                    <option value="newest">Newest First</option>
                                    <option value="price-low">Price: Low-High</option>
                                    <option value="price-high">Price: High-Low</option>
                                    <option value="name-az">Name: A-Z</option>
                                </select>
                            </div>

                            <div className="header-filter-group">
                                <select
                                    className={`sort-select-desktop ${seasonID ? 'has-value' : ''}`}
                                    value={seasonID || ''}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        const params = new URLSearchParams(searchParams);
                                        if (val) params.set('seasonID', val);
                                        else params.delete('seasonID');
                                        navigate(`/shop?${params.toString()}`);
                                    }}
                                    aria-label="Filter by Season"
                                >
                                    <option value="">All Seasons</option>
                                    {seasons.filter(s => s.selected === 1 || s.selected === true).map(s => (
                                        <option key={s.id} value={s.id}>{s.name} {s.year}</option>
                                    ))}
                                </select>
                            </div>

                            {hasActiveFilters && (
                                <button
                                    type="button"
                                    className="header-clear-btn"
                                    onClick={clearFilters}
                                    title="Reset all filters"
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                    <span>Reset</span>
                                </button>
                            )}
                        </div>


                        <button
                            className="sidebar-toggle-btn"
                            onClick={() => setSidebarOpen(o => !o)}
                            aria-expanded={sidebarOpen}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
                            </svg>
                            <span>Filter</span>
                            {hasActiveFilters && <span className="filter-dot" />}
                        </button>
                    </div>
                </div>

                {/* toolbar */}
                <div className="shop-toolbar">
                    <div className="header-price-range">
                        <div className="price-inputs mini">
                            <input
                                type="number"
                                placeholder="Min $"
                                value={priceMin}
                                onChange={e => setPriceMin(e.target.value)}
                                className="price-input"
                            />
                            <span className="price-sep">—</span>
                            <input
                                type="number"
                                placeholder="Max $"
                                value={priceMax}
                                onChange={e => setPriceMax(e.target.value)}
                                className="price-input"
                            />
                        </div>
                    </div>

                    <div className="toolbar-right">

                        <select
                            className="sort-select-inline"
                            value={sortBy}
                            onChange={e => setSortBy(e.target.value)}
                            aria-label="Sort products"
                        >
                            <option value="newest">Newest</option>
                            <option value="price-low">Price ↑</option>
                            <option value="price-high">Price ↓</option>
                            <option value="name-az">A → Z</option>
                        </select>
                    </div>
                </div>

                {/* grid */}
                {loading ? (
                    <div className="products-grid">
                        {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
                    </div>
                ) : currentProducts.length > 0 ? (
                    <>
                        <div className="products-grid">
                            {currentProducts.map((product, i) => {
                                const offerDetails = parseOfferDetails(product.offerDetails);
                                return (
                                    <article
                                        key={product.id}
                                        className="product-card"
                                        style={{ animationDelay: `${i * 0.05}s` }}
                                        onClick={() => setSelectedProduct(product)}
                                        tabIndex={0}
                                        onKeyDown={e => e.key === 'Enter' && setSelectedProduct(product)}
                                        aria-label={`View ${product.name}`}
                                    >
                                        <div className="product-image-wrapper">
                                            {product.varPhoto || product.photo ? (
                                                <img
                                                    src={`${API_BASE_URL}${product.varPhoto || product.photo}`}
                                                    alt={product.name}
                                                    className="product-image"
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <div className="product-image product-placeholder">
                                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3">
                                                        <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" />
                                                        <polyline points="21 15 16 10 5 21" />
                                                    </svg>
                                                </div>
                                            )}

                                            {isProductOutOfStock(product) ? (
                                                <div className="badge-out-of-stock-ribbon">Out of Stock</div>
                                            ) : product.can_preorder === 1 ? (
                                                <div className="badge-pre-order-ribbon">Pre Order</div>
                                            ) : null}
                                            <div className={`card-badges ${isProductOutOfStock(product) || product.can_preorder === 1 ? 'has-oos-ribbon' : ''}`}>
                                                {isNew(product.Date) && <span className="badge badge-new">New</span>}
                                                {product.discount_is_active === 1 && parseFloat(product.discount_value) > 0 && (
                                                    <span className="badge badge-sale">
                                                        {product.discount_type === 'percentage' ? `-${Math.round(product.discount_value)}%` : `-$${product.discount_value}`}
                                                    </span>
                                                )}
                                                {product.variationDiscountCount > 0 && !(product.discount_is_active === 1 && parseFloat(product.discount_value) > 0) && (
                                                    <span className="badge badge-var-sale">
                                                        Sale on {product.onSaleVariants}
                                                    </span>
                                                )}
                                                {product.activeOfferCount > 0 && offerDetails ? (
                                                    <span className="badge badge-offer">
                                                        {offerDetails.groupId ? 'Mix & Match: ' : ''}Buy {offerDetails.qty} for ${offerDetails.price.toFixed(2)}
                                                    </span>
                                                ) : product.activeOfferCount > 0 ? (
                                                    <span className="badge badge-sale" style={{ background: '#2563eb' }}>
                                                        Special Offer
                                                    </span>
                                                ) : null}
                                                {product.hasVariations && <span className="badge badge-var">Multiple Options</span>}
                                            </div>

                                            <div className="card-hover-overlay">
                                                <span className="quick-view-label">Quick View</span>
                                            </div>
                                        </div>

                                        <div className="product-info">
                                            {product.categoryName && (
                                                <span className="product-category">{product.categoryName}</span>
                                            )}
                                            <h3 className="product-name">{product.name}</h3>
                                            <div className="product-price-row">
                                                {product.discount_is_active === 1 && parseFloat(product.discount_value) > 0 ? (
                                                    <>
                                                        <p className="product-price discounted">${calculateDiscountedPrice(product.Price, product.discount_type, product.discount_value).toFixed(2)}</p>
                                                        <p className="product-price-old">${parseFloat(product.Price || 0).toFixed(2)}</p>
                                                    </>
                                                ) : (
                                                    <p className="product-price">${parseFloat(product.Price || 0).toFixed(2)}</p>
                                                )}
                                            </div>
                                            {product.activeOfferCount > 0 && offerDetails && (
                                                <p className="product-offer-text">
                                                    {offerDetails.groupId ? 'Group Offer' : 'Offer'}: {offerDetails.qty} for ${offerDetails.price.toFixed(2)}
                                                </p>
                                            )}
                                        </div>
                                    </article>
                                );
                            })}
                        </div>

                        {totalPages > 1 && (
                            <div className="pagination">
                                <button
                                    className="pagination-btn prev"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    aria-label="Previous page"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M15 18l-6-6 6-6" />
                                    </svg>
                                </button>

                                <div className="pagination-numbers">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                                        <button
                                            key={number}
                                            className={`pagination-number ${currentPage === number ? 'active' : ''}`}
                                            onClick={() => handlePageChange(number)}
                                        >
                                            {number}
                                        </button>
                                    ))}
                                </div>

                                <button
                                    className="pagination-btn next"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    aria-label="Next page"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M9 18l6-6-6-6" />
                                    </svg>
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="empty-state">
                        <div className="empty-icon">
                            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                        </div>
                        <h2>No items found</h2>
                        <p>Try adjusting your filters or search term.</p>
                        <button className="btn-primary" onClick={clearFilters}>
                            Clear Filters
                        </button>
                    </div>
                )}
            </div>

            {/* ── quick-view modal ──────────────────────────────── */}
            {selectedProduct && (
                <div
                    className="qv-overlay"
                    onClick={() => setSelectedProduct(null)}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Product quick view"
                >
                    <div
                        className="qv-panel"
                        ref={modalRef}
                        onClick={e => e.stopPropagation()}
                    >
                        <button
                            className="qv-close"
                            onClick={() => setSelectedProduct(null)}
                            aria-label="Close"
                        >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="1" y1="1" x2="19" y2="19" /><line x1="19" y1="1" x2="1" y2="19" />
                            </svg>
                        </button>

                        <div className="qv-body">
                            {/* image */}
                            <div className="qv-image-col">
                                <div className="qv-image-frame">
                                    {(() => {
                                        const varPhoto = productVariations.find(v => v.Color === selectedColor && v.photo)?.photo;
                                        const displayPhoto = varPhoto || selectedProduct.varPhoto || selectedProduct.photo;

                                        let isOutOfStock = false;
                                        if (!productVariationsLoading) {
                                            if (productVariations.length > 0) {
                                                const matchedVar = productVariations.find(v =>
                                                    (!selectedColor || v.Color === selectedColor) &&
                                                    (!selectedSize || v.Size === selectedSize)
                                                );
                                                isOutOfStock = matchedVar ? parseInt(matchedVar.Qty || 0) === 0 : true;
                                            } else {
                                                isOutOfStock = parseInt(selectedProduct.StockQTY || 0) === 0;
                                            }
                                        } else {
                                            isOutOfStock = false; // while variations are loading, avoid showing OOS
                                        }

                                        if (displayPhoto) {
                                            const displayUrl = `${API_BASE_URL}${displayPhoto}`;
                                            const isLoaded = !!preloadedPhotos[displayUrl];
                                            return (
                                                <img
                                                    src={displayUrl}
                                                    alt={selectedProduct.name}
                                                    className={`qv-image ${isOutOfStock && selectedProduct.can_preorder !== 1 ? 'out-of-stock-img' : ''} ${isLoaded ? 'qv-image-loaded' : ''}`}
                                                    onLoad={() => setPreloadedPhotos(prev => ({ ...prev, [displayUrl]: true }))}
                                                />
                                            );
                                        }

                                        return (
                                            <div className="qv-image product-placeholder">
                                                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.25">
                                                    <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" />
                                                    <polyline points="21 15 16 10 5 21" />
                                                </svg>
                                            </div>
                                        );
                                    })()}
                                    {isNew(selectedProduct.Date) && (
                                        <span className="badge badge-new qv-badge">New</span>
                                    )}
                                </div>
                            </div>

                            {/* details */}
                            <div className="qv-details">
                                {(activeDeliverySetting === null || parseFloat(activeDeliverySetting?.free_delivery_min) > 0) && (
                                    <span className="qv-shipping-info">
                                        Free shipping on orders over ${activeDeliverySetting?.free_delivery_min || '150'}
                                    </span>
                                )}
                                {selectedProduct.categoryName && (
                                    <span className="qv-category">{selectedProduct.categoryName}</span>
                                )}
                                <h2 className="qv-title">{selectedProduct.name}</h2>

                                {selectedProduct.description && (
                                    <p className="qv-desc">{selectedProduct.description}</p>
                                )}

                                {(() => {
                                    const matchedVar = productVariations.find(v =>
                                        (!selectedColor || v.Color === selectedColor) &&
                                        (!selectedSize || v.Size === selectedSize)
                                    );
                                    const discount = getEffectiveDiscount(selectedProduct, matchedVar);
                                    const price = parseFloat(selectedProduct.Price || 0);

                                    return (
                                        <div className="qv-price-row">
                                            {discount ? (
                                                <>
                                                    <p className="qv-price discounted">
                                                        ${calculateDiscountedPrice(price, discount.type, discount.value).toFixed(2)}
                                                    </p>
                                                    <p className="qv-price-old">${price.toFixed(2)}</p>
                                                    <span className="qv-sale-tag">
                                                        {discount.type === 'percentage' ? `SAVE ${Math.round(discount.value)}%` : `SAVE $${discount.value}`}
                                                    </span>
                                                </>
                                            ) : (
                                                <p className="qv-price">${price.toFixed(2)}</p>
                                            )}
                                        </div>
                                    );
                                })()}



                                {/* VARIATIONS */}
                                {productVariations.length > 0 && (
                                    <div className="qv-variations">
                                        {/* Colors */}
                                        {(() => {
                                            const uniqueColors = [...new Set(productVariations.map(v => v.Color).filter(Boolean))];
                                            if (uniqueColors.length === 0) return null;

                                            return (
                                                <div className="qv-var-group">
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '8px' }}>
                                                        <span className="qv-var-label" style={{ margin: 0 }}>Color: <strong>{selectedColor}</strong></span>
                                                        {selectedProduct.typeName && (
                                                            <span className="qv-fabric-tag" style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                                                                Fabric: <strong style={{ color: 'var(--text-primary)', fontWeight: '600' }}>{selectedProduct.typeName}</strong>
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="qv-var-options">
                                                        {uniqueColors.map(color => {
                                                            const colorStock = productVariations
                                                                .filter(v => v.Color === color)
                                                                .reduce((acc, v) => acc + parseInt(v.Qty || 0), 0);
                                                            const outOfStock = colorStock === 0;
                                                            const applyOutOfStockStyle = outOfStock;

                                                            return (
                                                                <button
                                                                    key={color}
                                                                    className={`qv-var-btn qv-color-btn ${selectedColor === color ? 'active' : ''} ${applyOutOfStockStyle ? 'out-of-stock' : ''}`}
                                                                    onClick={() => {
                                                                        setSelectedColor(color);
                                                                        const sizesForColor = sortSizes([...new Set(productVariations.filter(v => v.Color === color).map(v => v.Size).filter(Boolean))]);
                                                                        if (!sizesForColor.includes(selectedSize) && sizesForColor.length > 0) {
                                                                            setSelectedSize(sizesForColor[0]);
                                                                        }
                                                                    }}
                                                                    title={color}
                                                                    aria-label={color}
                                                                >
                                                                    <span
                                                                        className="qv-swatch"
                                                                        style={{ backgroundColor: getColorHex(color) || '#ccc' }}
                                                                    />
                                                                    {applyOutOfStockStyle && <span className="qv-var-x" />}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {/* Sizes */}
                                        {(() => {
                                            const availableSizes = sortSizes([...new Set(productVariations.filter(v => !selectedColor || v.Color === selectedColor).map(v => v.Size).filter(Boolean))]);
                                            if (availableSizes.length === 0) return null;

                                            return (
                                                <div className="qv-var-group">
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '8px' }}>
                                                        <span className="qv-var-label" style={{ margin: 0 }}>Size: <strong>{selectedSize}</strong></span>
                                                        {(sizeScales.length > 0 || selectedProduct?.size_guide_image) && (
                                                            <button
                                                                onClick={() => setIsSizeGuideOpen(true)}
                                                                style={{
                                                                    background: 'rgba(255, 59, 48, 0.06)',
                                                                    border: '1px solid rgba(255, 59, 48, 0.12)',
                                                                    color: '#ff3b30',
                                                                    fontSize: '0.76rem',
                                                                    fontWeight: '600',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '5px',
                                                                    padding: '5px 12px',
                                                                    borderRadius: '30px',
                                                                    transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                                                                    outline: 'none',
                                                                    boxShadow: '0 2px 6px rgba(255, 59, 48, 0.03)'
                                                                }}
                                                                onMouseEnter={e => {
                                                                    e.currentTarget.style.background = 'rgba(255, 59, 48, 0.12)';
                                                                    e.currentTarget.style.transform = 'scale(1.03)';
                                                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 59, 48, 0.06)';
                                                                }}
                                                                onMouseLeave={e => {
                                                                    e.currentTarget.style.background = 'rgba(255, 59, 48, 0.06)';
                                                                    e.currentTarget.style.transform = 'scale(1)';
                                                                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(255, 59, 48, 0.03)';
                                                                }}
                                                            >
                                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '3px' }}>
                                                                    <rect x="2" y="7" width="20" height="10" rx="2" ry="2" />
                                                                    <line x1="6" y1="7" x2="6" y2="11" />
                                                                    <line x1="10" y1="7" x2="10" y2="11" />
                                                                    <line x1="14" y1="7" x2="14" y2="11" />
                                                                    <line x1="18" y1="7" x2="18" y2="11" />
                                                                </svg>Size Guide
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="qv-var-options">
                                                        {availableSizes.map(size => {
                                                            const matchedVar = productVariations.find(v => v.Color === selectedColor && v.Size === size);
                                                            const outOfStock = matchedVar ? parseInt(matchedVar.Qty || 0) === 0 : true;
                                                            const applyOutOfStockStyle = outOfStock;

                                                            return (
                                                                <button
                                                                    key={size}
                                                                    className={`qv-var-btn ${selectedSize === size ? 'active' : ''} ${applyOutOfStockStyle ? 'out-of-stock' : ''}`}
                                                                    onClick={() => setSelectedSize(size)}
                                                                >
                                                                    {size}
                                                                    {applyOutOfStockStyle && <span className="qv-var-x" />}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {/* Size Chart Guide */}
                                        {false && sizeScales.length > 0 && (
                                            <div className="qv-var-group size-scales-chart-container" style={{ marginTop: '16px', borderTop: '1px solid rgba(255, 255, 255, 0.08)', paddingTop: '16px', width: '100%' }}>
                                                <span className="qv-var-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', fontWeight: '600', marginBottom: '10px', color: 'rgba(255,255,255,0.7)' }}>
                                                    📐 Garment Size Chart (cm)
                                                </span>
                                                <div style={{ overflowX: 'auto', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.2)' }}>
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', textAlign: 'center', color: 'rgba(255,255,255,0.85)' }}>
                                                        {(() => {
                                                            const groups = {};
                                                            sizeScales.forEach(s => {
                                                                const sName = s.size_name || s.size_id.toString();
                                                                const gName = getSizeGroup(sName);
                                                                if (!groups[gName]) {
                                                                    groups[gName] = {
                                                                        size_name: gName,
                                                                        length_cm: s.length_cm,
                                                                        breast_cm: s.breast_cm,
                                                                        waist_cm: s.waist_cm,
                                                                        hem_cm: s.hem_cm,
                                                                        sleeve_length_cm: s.sleeve_length_cm
                                                                    };
                                                                } else {
                                                                    if (s.length_cm) groups[gName].length_cm = s.length_cm;
                                                                    if (s.breast_cm) groups[gName].breast_cm = s.breast_cm;
                                                                    if (s.waist_cm) groups[gName].waist_cm = s.waist_cm;
                                                                    if (s.hem_cm) groups[gName].hem_cm = s.hem_cm;
                                                                    if (s.sleeve_length_cm) groups[gName].sleeve_length_cm = s.sleeve_length_cm;
                                                                }
                                                            });

                                                            const sortedGroupedScales = sortSizeGroups(Object.values(groups).map(g => ({ name: g.size_name })));
                                                            const uniqueSizesInScales = sortedGroupedScales.map(g => g.name);

                                                            const dimensions = [
                                                                { label: 'Length', field: 'length_cm' },
                                                                { label: 'Breast', field: 'breast_cm' },
                                                                { label: 'Waist', field: 'waist_cm' },
                                                                { label: 'Hem', field: 'hem_cm' },
                                                                { label: 'Sleeve', field: 'sleeve_length_cm' }
                                                            ];

                                                            return (
                                                                <>
                                                                    <thead>
                                                                        <tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                                                            <th style={{ padding: '8px 6px', fontWeight: '600', color: 'rgba(255,255,255,0.5)', textAlign: 'left', paddingLeft: '12px' }}>Size (cm)</th>
                                                                            {uniqueSizesInScales.map(sizeName => {
                                                                                const isCurrentSelected = selectedSize && getSizeGroup(selectedSize).toLowerCase() === sizeName.toLowerCase();
                                                                                return (
                                                                                    <th
                                                                                        key={sizeName}
                                                                                        style={{
                                                                                            padding: '8px 6px',
                                                                                            fontWeight: '600',
                                                                                            color: isCurrentSelected ? '#eb5e28' : 'white',
                                                                                            background: isCurrentSelected ? 'rgba(235, 94, 40, 0.12)' : 'transparent',
                                                                                            transition: 'all 0.2s'
                                                                                        }}
                                                                                    >
                                                                                        {sizeName}
                                                                                    </th>
                                                                                );
                                                                            })}
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {dimensions.map(dim => (
                                                                            <tr key={dim.field} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                                                                <td style={{ padding: '8px 6px', fontWeight: '600', color: 'rgba(255,255,255,0.6)', textAlign: 'left', paddingLeft: '12px' }}>{dim.label}</td>
                                                                                {uniqueSizesInScales.map(sizeName => {
                                                                                    const scale = groups[sizeName] || {};
                                                                                    const isCurrentSelected = selectedSize && getSizeGroup(selectedSize).toLowerCase() === sizeName.toLowerCase();
                                                                                    return (
                                                                                        <td
                                                                                            key={sizeName}
                                                                                            style={{
                                                                                                padding: '8px 6px',
                                                                                                background: isCurrentSelected ? 'rgba(235, 94, 40, 0.15)' : 'transparent',
                                                                                                fontWeight: isCurrentSelected ? '700' : 'normal',
                                                                                                color: isCurrentSelected ? '#eb5e28' : 'inherit',
                                                                                                transition: 'all 0.2s'
                                                                                            }}
                                                                                        >
                                                                                            {scale[dim.field] || '—'}
                                                                                        </td>
                                                                                    );
                                                                                })}
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </>
                                                            );
                                                        })()}
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* QUANTITY SELECTOR */}
                                {(() => {
                                    let stock = selectedProduct.StockQTY;
                                    let matchedVar = null;
                                    if (productVariations.length > 0) {
                                        matchedVar = productVariations.find(v =>
                                            (!selectedColor || v.Color === selectedColor) &&
                                            (!selectedSize || v.Size === selectedSize)
                                        );
                                        stock = matchedVar ? matchedVar.Qty : 0;
                                    }
                                    const stockNum = parseInt(stock || 0);
                                    const cartItem = cart.find(item => item.product.id === selectedProduct.id && item.variation?.varID === (matchedVar ? matchedVar.varID : null));
                                    const qtyInCart = cartItem ? cartItem.quantity : 0;
                                    const maxQty = stockNum;
                                    const availableStock = Math.max(0, maxQty - qtyInCart);

                                    return stockNum > 0 ? (
                                        <div className="qv-var-group" style={{ marginTop: '0', marginBottom: '10px', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                            <div className="qv-qty-controls">
                                                <button
                                                    className="qv-qty-btn"
                                                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                                                    disabled={quantity <= 1}
                                                    aria-label="Decrease quantity"
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                                                </button>
                                                <span className="qv-qty-display">{quantity}</span>
                                                <button
                                                    className="qv-qty-btn"
                                                    onClick={() => setQuantity(q => Math.min(availableStock === Infinity ? 99 : availableStock, q + 1))}
                                                    disabled={quantity >= availableStock}
                                                    aria-label="Increase quantity"
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                                                </button>
                                            </div>
                                            {selectedProduct.activeOfferCount > 0 && (() => {
                                                const offerDetails = parseOfferDetails(selectedProduct.offerDetails);
                                                return offerDetails ? (
                                                    <span className="qv-offer-badge">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: '6px' }}>
                                                            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                                                            <line x1="7" y1="7" x2="7.01" y2="7" />
                                                        </svg>
                                                        {offerDetails.groupId ? 'Set Offer' : 'Special Offer'}: Buy {offerDetails.qty} for ${offerDetails.price.toFixed(2)}
                                                    </span>
                                                ) : (
                                                    <span className="qv-offer-badge">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: '6px' }}>
                                                            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                                                            <line x1="7" y1="7" x2="7.01" y2="7" />
                                                        </svg>
                                                        Special Offer
                                                    </span>
                                                );
                                            })()}
                                            {stockNum <= 2 && (
                                                <div className="stock-badge in-stock">
                                                    <span className="stock-dot" />
                                                    Only {stockNum} left!
                                                </div>
                                            )}
                                        </div>
                                    ) : null;
                                })()}

                                <div className="qv-meta">
                                    {(() => {
                                        if (productVariationsLoading) return null;
                                        let stock = selectedProduct.StockQTY;

                                        if (productVariations.length > 0) {
                                            const matchedVar = productVariations.find(v =>
                                                (!selectedColor || v.Color === selectedColor) &&
                                                (!selectedSize || v.Size === selectedSize)
                                            );
                                            stock = matchedVar ? matchedVar.Qty : 0;
                                        }

                                        const stockNum = parseInt(stock || 0);
                                        const isOutOfStock = stockNum === 0;

                                        if (isOutOfStock) {
                                            return (
                                                <div className="stock-badge out-stock">
                                                    <span className="stock-dot" />
                                                    Out of Stock
                                                </div>
                                            );
                                        }

                                        return null;
                                    })()}
                                </div>

                                <div className="qv-actions">
                                    <button
                                        className="btn-primary btn-large"
                                        disabled={productVariationsLoading || (() => {
                                            let stock = selectedProduct.StockQTY;
                                            let matchedVar = null;
                                            if (productVariations.length > 0) {
                                                matchedVar = productVariations.find(v =>
                                                    (!selectedColor || v.Color === selectedColor) &&
                                                    (!selectedSize || v.Size === selectedSize)
                                                );
                                                stock = matchedVar ? matchedVar.Qty : 0;
                                            }
                                            const stockNum = parseInt(stock || 0);
                                            const cartItem = cart.find(item => item.product.id === selectedProduct.id && item.variation?.varID === (matchedVar ? matchedVar.varID : null));
                                            const qtyInCart = cartItem ? cartItem.quantity : 0;
                                            const maxQty = stockNum;
                                            const availableStock = Math.max(0, maxQty - qtyInCart);
                                            return maxQty === 0 || quantity > availableStock;
                                        })()}
                                        onClick={() => {
                                            let matchedVar = null;
                                            if (productVariations.length > 0) {
                                                matchedVar = productVariations.find(v =>
                                                    (!selectedColor || v.Color === selectedColor) &&
                                                    (!selectedSize || v.Size === selectedSize)
                                                );
                                            }


                                            // 2. Editing mode
                                            const editingOrderId = sessionStorage.getItem('editingOrderId');
                                            if (editingOrderId) {
                                                const storedItems = JSON.parse(sessionStorage.getItem('editingOrderItems') || '[]');
                                                const existsIdx = storedItems.findIndex(item =>
                                                    item.product_id === selectedProduct.id &&
                                                    item.variation_id === (matchedVar ? matchedVar.varID : null)
                                                );

                                                if (existsIdx > -1) {
                                                    // Update quantity in place!
                                                    storedItems[existsIdx].quantity = quantity;

                                                    // Update total for this item
                                                    let unitPrice = parseFloat(storedItems[existsIdx].price) || 0;
                                                    let unitDiscount = 0;
                                                    if (storedItems[existsIdx].discount_type === 'percentage') {
                                                        unitDiscount = unitPrice * (parseFloat(storedItems[existsIdx].discount_value) / 100);
                                                    } else if (storedItems[existsIdx].discount_type === 'fixed') {
                                                        unitDiscount = parseFloat(storedItems[existsIdx].discount_value || 0);
                                                    }
                                                    storedItems[existsIdx].discount = unitDiscount * quantity;
                                                    storedItems[existsIdx].total = (unitPrice * quantity) - storedItems[existsIdx].discount;

                                                    sessionStorage.setItem('editingOrderItems', JSON.stringify(storedItems));
                                                    setSelectedProduct(null);
                                                    navigate('/orders');
                                                } else {
                                                    const discountType = selectedProduct.discount_is_active === 1 ? selectedProduct.discount_type : null;
                                                    const discountValue = selectedProduct.discount_is_active === 1 ? selectedProduct.discount_value : 0;

                                                    storedItems.push({
                                                        product_id: selectedProduct.id,
                                                        variation_id: matchedVar ? matchedVar.varID : null,
                                                        product_name: selectedProduct.name,
                                                        product_photo: matchedVar && matchedVar.photo ? matchedVar.photo : selectedProduct.photo,
                                                        quantity: quantity,
                                                        price: selectedProduct.Price,
                                                        discount_type: discountType,
                                                        discount_value: discountValue,
                                                        offer_qty: selectedProduct.offer_qty,
                                                        offer_price: selectedProduct.offer_price,
                                                        discount: 0,
                                                        total: 0
                                                    });
                                                    sessionStorage.setItem('editingOrderItems', JSON.stringify(storedItems));
                                                    setSelectedProduct(null);
                                                    navigate('/orders');
                                                }
                                            } else {
                                                addToCart(selectedProduct, matchedVar, quantity);
                                                setSelectedProduct(null); // Close modal
                                                setNotification({ message: 'Added to your bag!', type: 'success' });
                                            }
                                        }}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                                            <line x1="3" y1="6" x2="21" y2="6" />
                                            <path d="M16 10a4 4 0 0 1-8 0" />
                                        </svg>
                                        {(() => {
                                            if (productVariationsLoading) return 'Loading...';
                                            let stock = selectedProduct.StockQTY;
                                            let matchedVar = null;
                                            if (productVariations.length > 0) {
                                                matchedVar = productVariations.find(v =>
                                                    (!selectedColor || v.Color === selectedColor) &&
                                                    (!selectedSize || v.Size === selectedSize)
                                                );
                                                stock = matchedVar ? matchedVar.Qty : 0;
                                            }
                                            const stockNum = parseInt(stock || 0);
                                            const cartItem = cart.find(item => item.product.id === selectedProduct.id && item.variation?.varID === (matchedVar ? matchedVar.varID : null));
                                            const qtyInCart = cartItem ? cartItem.quantity : 0;
                                            const maxQty = stockNum;
                                            const availableStock = Math.max(0, maxQty - qtyInCart);

                                            if (stockNum === 0) {
                                                return 'Sold Out';
                                            } else if (availableStock === 0) {
                                                return 'Max added to Bag';
                                            } else if (quantity > availableStock) {
                                                return `Only ${availableStock} more available`;
                                            }


                                            if (sessionStorage.getItem('editingOrderId')) {
                                                const storedItems = JSON.parse(sessionStorage.getItem('editingOrderItems') || '[]');
                                                const exists = storedItems.find(item =>
                                                    item.product_id === selectedProduct.id &&
                                                    item.variation_id === (matchedVar ? matchedVar.varID : null)
                                                );
                                                return exists ? 'Update Quantity in Order' : 'Add to Order';
                                            }

                                            return selectedProduct.can_preorder === 1 ? 'Pre-Order Now' : 'Add to Bag';
                                        })()}
                                    </button>
                                    <button className="btn-outline btn-wishlist" onClick={handleAddToWishlist} aria-label="Save to Wishlist">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                                        </svg>
                                        <span>Save to Wishlist</span>
                                    </button>
                                </div>


                            </div>
                        </div>
                    </div>
                </div>
            )}
            {isSizeGuideOpen && (
                <div
                    className="modal-overlay size-guide-overlay"
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 10000,
                        background: 'rgba(0, 0, 0, 0.65)',
                        backdropFilter: 'blur(12px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    onClick={() => setIsSizeGuideOpen(false)}
                >
                    <style>{`
                        @keyframes modalFadeIn {
                            from { opacity: 0; transform: scale(0.95); }
                            to { opacity: 1; transform: scale(1); }
                        }
                        @media (max-width: 600px) {
                            .size-guide-overlay {
                                align-items: center !important;
                                justify-content: center !important;
                                padding-bottom: 80px !important;
                            }
                            .size-guide-modal {
                                width: 92% !important;
                                max-width: 92% !important;
                                border-radius: 20px !important;
                                padding: 24px 16px 24px 16px !important;
                                animation: modalFadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                                border: 1px solid #e5e5e7 !important;
                            }
                            .size-guide-modal th, .size-guide-modal td {
                                padding: 12px 6px !important;
                                font-size: 0.72rem !important;
                            }
                            .size-guide-drag-handle {
                                display: none !important;
                            }
                        }
                    `}</style>
                    <div
                        className="size-guide-modal"
                        style={{
                            width: '90%',
                            maxWidth: selectedProduct?.size_guide_image ? '700px' : '520px',
                            background: '#ffffff',
                            border: '1px solid #e5e5e7',
                            borderRadius: '16px',
                            padding: '24px',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
                            color: '#1d1d1f',
                            position: 'relative',
                            transition: 'max-width 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="size-guide-drag-handle" style={{ width: '36px', height: '5px', background: '#e5e5e7', borderRadius: '3px', margin: '-10px auto 16px auto', display: 'none' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #f5f5f7', paddingBottom: '12px' }}>
                            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#1d1d1f', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ff3b30" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px' }}>
                                    <rect x="2" y="7" width="20" height="10" rx="2" ry="2" />
                                    <line x1="6" y1="7" x2="6" y2="11" />
                                    <line x1="10" y1="7" x2="10" y2="11" />
                                    <line x1="14" y1="7" x2="14" y2="11" />
                                    <line x1="18" y1="7" x2="18" y2="11" />
                                </svg><span style={{ color: '#1d1d1f' }}>Size Chart</span> <span style={{ color: '#ff3b30' }}>Guide</span>
                            </h3>
                            <button
                                onClick={() => setIsSizeGuideOpen(false)}
                                style={{ background: '#f5f5f7', border: 'none', width: '28px', height: '28px', borderRadius: '50%', color: '#86868b', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', transition: 'all 0.2s' }}
                                onMouseEnter={e => e.target.style.background = '#e8e8ed'}
                                onMouseLeave={e => e.target.style.background = '#f5f5f7'}
                            >
                                ✕
                            </button>
                        </div>
                        <p style={{ fontSize: '0.8rem', color: '#86868b', marginTop: '-8px', marginBottom: '16px', lineHeight: '1.4' }}>
                            {selectedProduct?.size_guide_image ? (
                                <>Refer to the official size guide chart below for <strong>{selectedProduct?.name}</strong>.</>
                            ) : (
                                <>Garment dimensions are in centimeters (cm) for <strong>{selectedProduct?.name}</strong>.</>
                            )}
                        </p>

                        {selectedProduct?.size_guide_image ? (
                            <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e5e5e7', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <img
                                    src={`${API_BASE_URL}${selectedProduct.size_guide_image}`}
                                    alt={`${selectedProduct.name} Size Guide`}
                                    style={{ width: '100%', height: 'auto', display: 'block' }}
                                />
                            </div>
                        ) : (
                            <div style={{ overflowX: 'auto', borderRadius: '12px', border: '1px solid #e5e5e7', background: '#ffffff', width: '100%', WebkitOverflowScrolling: 'touch' }}>
                                <table style={{ width: '100%', minWidth: '400px', borderCollapse: 'collapse', fontSize: '0.78rem', textAlign: 'center', color: '#1d1d1f' }}>
                                    {(() => {
                                        const groups = {};
                                        sizeScales.forEach(s => {
                                            const sName = s.size_name || s.size_id.toString();
                                            const gName = getSizeGroup(sName);
                                            if (!groups[gName]) {
                                                groups[gName] = {
                                                    size_name: gName,
                                                    length_cm: s.length_cm,
                                                    breast_cm: s.breast_cm,
                                                    waist_cm: s.waist_cm,
                                                    hem_cm: s.hem_cm,
                                                    sleeve_length_cm: s.sleeve_length_cm
                                                };
                                            } else {
                                                if (s.length_cm) groups[gName].length_cm = s.length_cm;
                                                if (s.breast_cm) groups[gName].breast_cm = s.breast_cm;
                                                if (s.waist_cm) groups[gName].waist_cm = s.waist_cm;
                                                if (s.hem_cm) groups[gName].hem_cm = s.hem_cm;
                                                if (s.sleeve_length_cm) groups[gName].sleeve_length_cm = s.sleeve_length_cm;
                                            }
                                        });

                                        const sortedGroupedScales = sortSizeGroups(Object.values(groups).map(g => ({ name: g.size_name })));
                                        const uniqueSizesInScales = sortedGroupedScales.map(g => g.name);

                                        const dimensions = [
                                            { label: 'Length', field: 'length_cm' },
                                            { label: 'Breast', field: 'breast_cm' },
                                            { label: 'Waist', field: 'waist_cm' },
                                            { label: 'Hem', field: 'hem_cm' },
                                            { label: 'Sleeve', field: 'sleeve_length_cm' }
                                        ];

                                        return (
                                            <>
                                                <thead>
                                                    <tr style={{ background: '#f5f5f7', borderBottom: '1px solid #e5e5e7' }}>
                                                        <th style={{ padding: '10px 8px', fontWeight: '600', color: '#86868b', textAlign: 'left', paddingLeft: '12px' }}>Size (cm)</th>
                                                        {uniqueSizesInScales.map(sizeName => {
                                                            const isCurrentSelected = selectedSize && getSizeGroup(selectedSize).toLowerCase() === sizeName.toLowerCase();
                                                            return (
                                                                <th
                                                                    key={sizeName}
                                                                    style={{
                                                                        padding: '10px 8px',
                                                                        fontWeight: '700',
                                                                        color: isCurrentSelected ? '#ff3b30' : '#1d1d1f',
                                                                        background: isCurrentSelected ? 'rgba(255, 59, 48, 0.08)' : 'transparent',
                                                                        transition: 'all 0.2s'
                                                                    }}
                                                                >
                                                                    {sizeName}
                                                                </th>
                                                            );
                                                        })}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {dimensions.map(dim => (
                                                        <tr key={dim.field} style={{ borderBottom: '1px solid #f5f5f7' }}>
                                                            <td style={{ padding: '10px 8px', fontWeight: '600', color: '#86868b', textAlign: 'left', paddingLeft: '12px' }}>{dim.label}</td>
                                                            {uniqueSizesInScales.map(sizeName => {
                                                                const scale = groups[sizeName] || {};
                                                                const isCurrentSelected = selectedSize && getSizeGroup(selectedSize).toLowerCase() === sizeName.toLowerCase();
                                                                return (
                                                                    <td
                                                                        key={sizeName}
                                                                        style={{
                                                                            padding: '10px 8px',
                                                                            background: isCurrentSelected ? 'rgba(255, 59, 48, 0.08)' : 'transparent',
                                                                            fontWeight: isCurrentSelected ? '700' : 'normal',
                                                                            color: isCurrentSelected ? '#ff3b30' : '#1d1d1f',
                                                                            transition: 'all 0.2s'
                                                                        }}
                                                                    >
                                                                        {scale[dim.field] || '—'}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </>
                                        );
                                    })()}
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {notification && (
                <Toast
                    message={notification.message}
                    type={notification.type}
                    onClose={() => setNotification(null)}
                />
            )}
        </div>
    );
};

export default Shop;
